import { mapState } from 'vuex';
import { debounce } from 'lodash';

export default {
	data() {
		return {
			logoutDelay: 30 * 60 * 1000, 
			warningDelay: 25 * 60 * 1000, 
			events: [
				'mousedown',
				'mousemove',
				'keypress',
				'scroll',
				'touchstart',
                'click'
			],
            debouncedReset: null,
            inactivityCheckInterval: null,
            warningShown: false
		};
	},

	computed: {
		...mapState(['currentRoleLevel']),
		isAuthenticated() {
			return this.$store.getters.isAuthenticated;
		},
	},

	watch: {
		isAuthenticated(newVal) {
			if (newVal) {
				this.hasLoggedOut = false;
				this.setupInactivityCheck();
			}
			  else {
			    this.cleanupInactivityCheck();
			  }
		},
	},

	mounted() {
		window.addEventListener('storage', this.handleStorageEvent);
		if (this.isAuthenticated) {
			this.setupInactivityCheck();
		}
	},
	beforeDestroy() {
		this.cleanupInactivityCheck();
		window.removeEventListener('storage', this.handleStorageEvent);
	},

	methods: {
		setupInactivityCheck() {            
            this.debouncedReset = debounce(this.updateLastActivity, 100);
            
            this.events.forEach((event) => {
                window.addEventListener(event, this.debouncedReset);
            });
			
			localStorage.setItem('lastActivity', Date.now().toString());
			
			this.inactivityCheckInterval = setInterval(this.checkInactivity, 5000);
		},
		updateLastActivity() {
			localStorage.setItem('lastActivity', Date.now().toString());
		},
		checkInactivity() {
			const lastActivity = parseInt(localStorage.getItem('lastActivity'), 10) || Date.now();
			const timeSinceLastActivity = Date.now() - lastActivity;
			
			if (timeSinceLastActivity >= this.logoutDelay) {
				this.logoutUser();
            } else if (timeSinceLastActivity >= this.warningDelay && !this.warningShown) {
				this.showWarningToast();
                this.warningShown = true;
			} else if (timeSinceLastActivity < this.warningDelay && this.warningShown) {
                this.warningShown = false;
            }
		},
		handleStorageEvent(event) {
			if (event.key === 'lastActivity') {
				this.checkInactivity();
			}
			else if (event.key === 'forceLogout') {
				this.logoutUser();
                localStorage.removeItem('forceLogout');
			}
		},

		logoutUser() {
			// Prevent multiple logout toasts
			if (this.hasLoggedOut) return;
			this.hasLoggedOut = true;

			if (!this.isAuthenticated) return;

			localStorage.setItem('forceLogout', Date.now().toString());
			this.cleanupInactivityCheck();

            const currentUserRole = this.currentRoleLevel?.roleAssociated;

			this.$store.dispatch('logout').then(() => {
				this.$bvToast.toast(
					'You have been logged out due to inactivity',
					{
						title: 'Session Timeout',
						variant: 'warning',
						solid: true,
					}
				);
				this.$router.push(currentUserRole === 'party' ? '/couple/auth' : '/auth');
				
				this.$store.commit('setLoggingOut', false);
			}).catch(error => {
				console.error('Logout error:', error);
				this.$bvToast.toast('Error during logout. Please try again.', {
					title: 'Error',
					variant: 'danger',
					solid: true,
				});
				this.hasLoggedOut = false;
			});
		},

		showWarningToast() {
			this.$bvToast.toast('Your session will expire in 5 minutes due to inactivity', {
				title: 'Session Expiring Soon',
				variant: 'warning',
				solid: true,
			});
		},


		cleanupInactivityCheck() {
            if (this.debouncedReset) {
                this.events.forEach((event) => {
                    window.removeEventListener(event, this.debouncedReset);
                });
                this.debouncedReset.cancel();
                this.debouncedReset = null;
            }

            clearInterval(this.inactivityCheckInterval);
            this.inactivityCheckInterval = null;
		},
	},
};
